import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Checkbox,
  CloseButton,
  Group,
  Image,
  Kbd,
  Paper,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { formatPrice } from "@livecommerce/ui";
import { useTranslation } from "react-i18next";
import type { CatalogueEntry, Settings } from "./api";
import { ageMinutes, availability, type Prepared, search } from "./search";
import styles from "./Catalogue.module.scss";

/**
 * The catalogue search (M1.13 §2) — the box an operator lives in during a show.
 *
 * **Instant**: every keystroke searches the index the panel already holds (`search.ts`), so the
 * results are there before the next key lands. There is no *Search* button because there is
 * nothing to wait for.
 *
 * **Keyboard first**, because the operator is listening to the presenter and not looking for the
 * mouse (`R-HOST-15`): `/` from anywhere on the page puts the cursor here, the arrow keys move
 * through the results, Enter picks one and Escape clears the box.
 *
 * **Only what can be featured, unless asked.** By default a product that would be refused — no
 * SKU, untracked, no location, below the minimum by the copy's figure — is not listed: during a
 * show it is a row to read and reject. *Show unavailable products* lists everything matching, each
 * marked with why it cannot be featured, which is how an operator finds out why the product in the
 * presenter's hand is not there. A product with no SKU is marked **Disabled**: it is never sold
 * from a show (`validSku`).
 *
 * The stock on each row is the copy's figure and its age. It is for choosing, not for deciding —
 * featuring reads Shopify again (`shows/featuring.ts`).
 */
export function CatalogueSearch({
  prepared,
  settings,
  pickedId,
  onPick,
  onClear,
  placeholder,
  eligible,
  shortcut = true,
}: {
  prepared: Prepared[];
  settings: Settings | undefined;
  /** The variant currently open in the sheet, drawn as chosen. */
  pickedId: string | undefined;
  onPick: (entry: CatalogueEntry) => void;
  /**
   * The box has been emptied — by its clear button, by Escape, or by deleting the text. The picker
   * closes the sheet, because a sheet for a product the search no longer shows is one the operator
   * can press Enter into by mistake.
   */
  onClear: () => void;
  placeholder: string;
  /**
   * What is listed without *Show unavailable products* ticked. Defaults to what can be featured;
   * the giveaway card passes its own rule, because a giveaway of one is not refused for being
   * below the feature minimum (`R-GIVE-7`).
   */
  eligible?: (entry: CatalogueEntry) => boolean;
  /**
   * Whether `/` focuses this box. Only one search on a page may own it — the feature search, which
   * is the one an operator reaches for mid-show — so a second search on the same page turns it off.
   */
  shortcut?: boolean;
}) {
  const { t } = useTranslation();
  const [term, setTerm] = useState("");
  const [cursor, setCursor] = useState(0);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const results = useMemo(
    () =>
      search(prepared, term, 30, (entry) =>
        showUnavailable
          ? true
          : eligible
            ? eligible(entry)
            : availability(entry, settings) === "available",
      ),
    [prepared, term, showUnavailable, settings, eligible],
  );
  // One clock for the whole list, so every age on screen agrees, ticking often enough for minutes.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  // `/` anywhere on the page, unless somebody is typing somewhere else.
  useEffect(() => {
    if (!shortcut) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      if (target?.isContentEditable) return;
      event.preventDefault();
      input.current?.focus();
      input.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shortcut]);

  const clamp = (index: number) =>
    Math.min(Math.max(index, 0), Math.max(results.length - 1, 0));

  /** Every way the box gets a new value goes through here, so emptying it always clears. */
  const change = (next: string) => {
    setTerm(next);
    setCursor(0);
    if (!next.trim()) onClear();
  };

  return (
    <Stack gap="xs">
      <TextInput
        ref={input}
        value={term}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(event) => change(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setCursor((at) => clamp(at + 1));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setCursor((at) => clamp(at - 1));
          } else if (event.key === "Enter") {
            const chosen = results[cursor];
            if (chosen) {
              event.preventDefault();
              onPick(chosen);
            }
          } else if (event.key === "Escape") {
            change("");
          }
        }}
        rightSection={
          term ? (
            <CloseButton
              size="sm"
              aria-label={t("catalogue.clear")}
              onClick={() => {
                change("");
                input.current?.focus();
              }}
            />
          ) : (
            <Kbd size="xs">/</Kbd>
          )
        }
        rightSectionPointerEvents={term ? "all" : "none"}
      />

      <Checkbox
        size="xs"
        label={t("catalogue.showUnavailable")}
        checked={showUnavailable}
        onChange={(event) => {
          setShowUnavailable(event.currentTarget.checked);
          setCursor(0);
        }}
      />

      {term && results.length === 0 ? (
        <Text size="xs" c="dimmed">
          {t(showUnavailable ? "catalogue.noResults" : "catalogue.noAvailableResults")}
        </Text>
      ) : null}

      <Stack gap={4} role="listbox" aria-label={t("catalogue.results")}>
        {results.map((entry, index) => (
          <ResultRow
            key={entry.variantId}
            entry={entry}
            settings={settings}
            now={now}
            active={index === cursor}
            chosen={entry.variantId === pickedId}
            onChoose={() => {
              setCursor(index);
              onPick(entry);
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}

function ResultRow({
  entry,
  settings,
  now,
  active,
  chosen,
  onChoose,
}: {
  entry: CatalogueEntry;
  settings: Settings | undefined;
  now: number;
  active: boolean;
  chosen: boolean;
  onChoose: () => void;
}) {
  const { t } = useTranslation();
  const age = ageMinutes(entry.stockSyncedAt, now);
  const state = availability(entry, settings);

  return (
    <Paper
      withBorder
      radius="md"
      p="xs"
      role="option"
      aria-selected={active}
      aria-disabled={state === "disabled"}
      className={chosen ? styles.chosen : active ? styles.active : styles.result}
      onClick={onChoose}
    >
      <Group gap="sm" wrap="nowrap">
        {entry.imageUrl ? (
          <Image
            src={entry.imageUrl}
            alt={entry.imageAlt ?? ""}
            w={40}
            h={40}
            radius="sm"
            fit="cover"
          />
        ) : null}
        <Stack gap={0} className={styles.grow}>
          <Text
            size="sm"
            fw={600}
            lineClamp={1}
            c={state === "disabled" ? "dimmed" : undefined}
          >
            {entry.variantTitle
              ? `${entry.productTitle} — ${entry.variantTitle}`
              : entry.productTitle}
          </Text>
          <Text size="xs" c="dimmed">
            {t("catalogue.meta", {
              sku: entry.sku?.trim() ? entry.sku : t("catalogue.noSku"),
              price: formatPrice(entry.price),
            })}
          </Text>
        </Stack>
        <Stack gap={0} align="flex-end">
          {state === "disabled" ? (
            <Badge size="xs" color="gray" variant="light">
              {t("catalogue.disabled")}
            </Badge>
          ) : state === "untracked" ? (
            <Badge size="xs" color="orange" variant="light">
              {t("catalogue.untracked")}
            </Badge>
          ) : state === "no-location" ? (
            <Badge size="xs" color="orange" variant="light">
              {t("catalogue.noLocation")}
            </Badge>
          ) : (
            <Text size="sm" fw={600} c={state === "scarce" ? "orange" : undefined}>
              {t("catalogue.available", { count: entry.available ?? 0 })}
            </Text>
          )}
          {(state === "available" || state === "scarce") && age !== null ? (
            <Text size="xs" c="dimmed">
              {age === 0
                ? t("catalogue.justNow")
                : t("catalogue.minutesAgo", { count: age })}
            </Text>
          ) : null}
        </Stack>
      </Group>
    </Paper>
  );
}
